const THEMES = ["brand", "muted", "success", "warning", "lime", "error"]
const ICON_OPTIONS = [
  { value: "check", label: "Check", iconClass: "icon-[lucide--check]" },
  { value: "shield", label: "Shield", iconClass: "icon-[lucide--shield]" },
  { value: "radio", label: "Radio", iconClass: "icon-[lucide--radio]" },
  { value: "book", label: "Book", iconClass: "icon-[lucide--book-open]" },
  { value: "wrench", label: "Wrench", iconClass: "icon-[lucide--wrench]" },
  { value: "sparkles", label: "Sparkles", iconClass: "icon-[lucide--sparkles]" }
]

export default class BannerWithIconTool {
  static get toolbox() {
    return {
      title: "Banner with icon",
      icon: "<span class=\"icon-[lucide--badge-check] size-4\"></span>"
    }
  }

  static get sanitize() {
    return {
      icon: false,
      title: false,
      subtitle: false,
      theme: false
    }
  }

  constructor({ data, readOnly } = {}) {
    this.readOnly = Boolean(readOnly)
    this.data = {
      icon: this.normalizeIcon(data?.icon),
      title: data?.title || "",
      subtitle: data?.subtitle || "",
      theme: this.normalizeTheme(data?.theme)
    }
    this.wrapper = null
    this.iconGlyph = null
    this.titleInput = null
    this.subtitleInput = null
    this.themeButtons = new Map()
    this.iconButtons = new Map()
  }

  render() {
    this.wrapper = document.createElement("div")
    this.wrapper.className = "editorjs-banner-with-icon"

    const iconWrap = document.createElement("div")
    iconWrap.className = "editorjs-banner-with-icon__icon"
    this.iconGlyph = document.createElement("span")
    this.iconGlyph.className = `${this.iconClassFor(this.data.icon)} size-8`
    this.iconGlyph.setAttribute("aria-hidden", "true")
    iconWrap.appendChild(this.iconGlyph)

    const body = document.createElement("div")
    body.className = "editorjs-banner-with-icon__body"

    this.titleInput = document.createElement("input")
    this.titleInput.type = "text"
    this.titleInput.placeholder = "Banner title"
    this.titleInput.className = "editorjs-banner-with-icon__title"
    this.titleInput.value = this.data.title
    this.titleInput.readOnly = this.readOnly
    this.titleInput.addEventListener("input", () => {
      this.data.title = this.titleInput.value
    })

    this.subtitleInput = document.createElement("textarea")
    this.subtitleInput.placeholder = "Banner subtitle"
    this.subtitleInput.className = "editorjs-banner-with-icon__subtitle"
    this.subtitleInput.value = this.data.subtitle
    this.subtitleInput.readOnly = this.readOnly
    this.subtitleInput.rows = 2
    this.subtitleInput.addEventListener("input", () => {
      this.data.subtitle = this.subtitleInput.value
    })

    body.append(this.titleInput, this.subtitleInput)

    this.wrapper.append(iconWrap, body)

    this.applyThemeClass()
    return this.wrapper
  }

  renderSettings() {
    const settings = document.createElement("div")
    settings.className = "editorjs-banner-with-icon-settings"

    const themeSection = document.createElement("div")
    themeSection.className = "editorjs-banner-with-icon-settings__section"
    const themeLabel = document.createElement("p")
    themeLabel.className = "editorjs-banner-with-icon-settings__label"
    themeLabel.textContent = "Theme"
    themeSection.appendChild(themeLabel)

    const themeButtons = document.createElement("div")
    themeButtons.className = "editorjs-banner-with-icon-settings__buttons"
    THEMES.forEach((theme) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "editorjs-banner-with-icon-settings__btn"
      button.textContent = this.themeLabel(theme)
      this.themeButtons.set(theme, button)
      button.addEventListener("click", () => {
        this.data.theme = theme
        this.applyThemeClass()
        this.updateSettingsButtonsState()
      })
      themeButtons.appendChild(button)
    })
    themeSection.appendChild(themeButtons)

    const iconSection = document.createElement("div")
    iconSection.className = "editorjs-banner-with-icon-settings__section"
    const iconLabel = document.createElement("p")
    iconLabel.className = "editorjs-banner-with-icon-settings__label"
    iconLabel.textContent = "Icon"
    iconSection.appendChild(iconLabel)

    const iconButtons = document.createElement("div")
    iconButtons.className = "editorjs-banner-with-icon-settings__buttons"
    ICON_OPTIONS.forEach((option) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "editorjs-banner-with-icon-settings__btn editorjs-banner-with-icon-settings__btn--icon"
      button.setAttribute("aria-label", option.label)
      button.innerHTML = `<span class="${option.iconClass} size-4" aria-hidden="true"></span>`
      this.iconButtons.set(option.value, button)
      button.addEventListener("click", () => {
        this.data.icon = option.value
        this.applyIconClass()
        this.updateSettingsButtonsState()
      })
      iconButtons.appendChild(button)
    })
    iconSection.appendChild(iconButtons)

    settings.append(themeSection, iconSection)
    this.updateSettingsButtonsState()
    return settings
  }

  save() {
    return {
      icon: this.normalizeIcon(this.data.icon),
      title: this.titleInput?.value || "",
      subtitle: this.subtitleInput?.value || "",
      theme: this.normalizeTheme(this.data.theme)
    }
  }

  validate(savedData) {
    return Boolean((savedData?.title || "").trim() || (savedData?.subtitle || "").trim())
  }

  applyThemeClass() {
    if (!this.wrapper) return
    THEMES.forEach((theme) => this.wrapper.classList.remove(`editorjs-banner-with-icon--${theme}`))
    this.wrapper.classList.add(`editorjs-banner-with-icon--${this.normalizeTheme(this.data.theme)}`)
  }

  applyIconClass() {
    if (!this.iconGlyph) return
    this.iconGlyph.className = `${this.iconClassFor(this.data.icon)} size-8`
  }

  updateSettingsButtonsState() {
    this.themeButtons.forEach((button, theme) => {
      button.classList.toggle("editorjs-banner-with-icon-settings__btn--active", theme === this.data.theme)
    })
    this.iconButtons.forEach((button, icon) => {
      button.classList.toggle("editorjs-banner-with-icon-settings__btn--active", icon === this.data.icon)
    })
  }

  normalizeTheme(theme) {
    return THEMES.includes(theme) ? theme : "brand"
  }

  normalizeIcon(icon) {
    return ICON_OPTIONS.some((option) => option.value === icon) ? icon : "check"
  }

  iconClassFor(icon) {
    return ICON_OPTIONS.find((option) => option.value === icon)?.iconClass || "icon-[lucide--check]"
  }

  themeLabel(theme) {
    switch (theme) {
    case "brand":
      return "Brand"
    case "muted":
      return "Muted"
    case "success":
      return "Success"
    case "warning":
      return "Warning"
    case "lime":
      return "Lime"
    case "error":
      return "Error"
    default:
      return "Brand"
    }
  }
}
